// src/components/manager-v2/TeamPerformanceAnalytics.js
import React, { useState, useEffect, useRef } from 'react';
import {
    LineChart, Line, BarChart, Bar, ScatterChart, Scatter, PieChart, Pie, Cell,
    AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ZAxis
} from 'recharts';
import {
    Users, TrendingUp, BarChart3, Loader, AlertCircle, Filter, Info,
    Download, RefreshCw, Award, Target, TrendingDown, Activity,
    ChevronDown, X
} from 'lucide-react';
import performanceService from '../../services/performance.service';
import ProductivityTrendChart from './ProductivityTrendChart';

const TeamPerformanceAnalytics = () => {
    const [teamPerformance, setTeamPerformance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showProductivityInfo, setShowProductivityInfo] = useState(false);

    // Advanced filters
    const [filters, setFilters] = useState({
        cycle: 'all',
        department: 'all',
        ratingRange: 'all',
        performanceStatus: 'all'
    });

    const [showFilters, setShowFilters] = useState(false);

    // Ref for scrolling to detailed table
    const detailedTableRef = useRef(null);

    // Scroll to detailed table function
    const scrollToDetailedTable = () => {
        if (detailedTableRef.current) {
            detailedTableRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    useEffect(() => {
        fetchTeamPerformance();
    }, []);

    const fetchTeamPerformance = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await performanceService.getTeamPerformance();
            setTeamPerformance(data.performance || []);
        } catch (err) {
            console.error('Error fetching team performance:', err);
            setError('Failed to load team performance data');
        } finally {
            setLoading(false);
        }
    };

    // Filter team performance data based on selected filters
    const getFilteredData = () => {
        return teamPerformance.filter(member => {
            const rating = parseFloat(member.rating) || 0;

            // Department filter
            if (filters.department !== 'all' && member.department_name !== filters.department) {
                return false;
            }

            // Rating range filter
            if (filters.ratingRange !== 'all') {
                if (filters.ratingRange === 'excellent' && rating < 4.5) return false;
                if (filters.ratingRange === 'good' && (rating < 4.0 || rating >= 4.5)) return false;
                if (filters.ratingRange === 'average' && (rating < 3.5 || rating >= 4.0)) return false;
                if (filters.ratingRange === 'below' && rating >= 3.5) return false;
            }

            // Performance status filter
            if (filters.performanceStatus !== 'all') {
                if (filters.performanceStatus === 'top' && rating < 4.5) return false;
                if (filters.performanceStatus === 'attention' && rating >= 3.5) return false;
            }

            return true;
        });
    };

    const filteredData = getFilteredData();

    // Get unique departments
    const departments = [...new Set(teamPerformance.map(m => m.department_name).filter(Boolean))];

    // Prepare team ratings distribution data
    const prepareRatingsDistribution = () => {
        const distribution = {
            'Excellent (4.5-5.0)': 0,
            'Good (4.0-4.4)': 0,
            'Average (3.5-3.9)': 0,
            'Below Average (3.0-3.4)': 0,
            'Needs Improvement (<3.0)': 0
        };

        filteredData.forEach(member => {
            const rating = parseFloat(member.rating) || 0;
            if (rating >= 4.5) distribution['Excellent (4.5-5.0)']++;
            else if (rating >= 4.0) distribution['Good (4.0-4.4)']++;
            else if (rating >= 3.5) distribution['Average (3.5-3.9)']++;
            else if (rating >= 3.0) distribution['Below Average (3.0-3.4)']++;
            else if (rating > 0) distribution['Needs Improvement (<3.0)']++;
        });

        return Object.entries(distribution).map(([name, value]) => ({ name, value }));
    };

    // Prepare productivity distribution data
    const prepareProductivityDistribution = () => {
        const distribution = {
            '90-100%': 0,
            '80-89%': 0,
            '70-79%': 0,
            '60-69%': 0,
            'Below 60%': 0
        };

        filteredData.forEach(member => {
            const productivity = member.productivity || 0;
            if (productivity >= 90) distribution['90-100%']++;
            else if (productivity >= 80) distribution['80-89%']++;
            else if (productivity >= 70) distribution['70-79%']++;
            else if (productivity >= 60) distribution['60-69%']++;
            else if (productivity > 0) distribution['Below 60%']++;
        });

        return Object.entries(distribution).map(([name, value]) => ({ name, value }));
    };

    // Prepare team member comparison data
    const prepareTeamComparison = () => {
        return filteredData
            .filter(member => member.rating && member.rating > 0)
            .sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating))
            .slice(0, 10) // Top 10 performers
            .map(member => ({
                name: `${member.first_name} ${member.last_name}`,
                rating: parseFloat(member.rating),
                productivity: member.productivity || 0,
                goalsCompleted: member.completed_goals || 0
            }));
    };

    // Prepare performance comparison data (rating vs productivity)
    const preparePerformanceComparison = () => {
        return filteredData
            .filter(member => member.rating && member.rating > 0)
            .sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating))
            .slice(0, 15) // Top 15 for better visibility
            .map(member => ({
                name: `${member.first_name.charAt(0)}. ${member.last_name}`,
                rating: (parseFloat(member.rating) * 20).toFixed(1), // Convert to 100 scale
                productivity: member.productivity || 0,
                ratingDisplay: parseFloat(member.rating).toFixed(1),
                goals: member.completed_goals || 0
            }));
    };

    // Prepare department comparison data
    const prepareDepartmentComparison = () => {
        const deptStats = {};

        filteredData.forEach(member => {
            const dept = member.department_name || 'Unassigned';
            if (!deptStats[dept]) {
                deptStats[dept] = {
                    department: dept,
                    totalMembers: 0,
                    totalRating: 0,
                    totalProductivity: 0
                };
            }

            deptStats[dept].totalMembers++;
            deptStats[dept].totalRating += parseFloat(member.rating) || 0;
            deptStats[dept].totalProductivity += member.productivity || 0;
        });

        return Object.values(deptStats).map(dept => ({
            department: dept.department,
            avgRating: dept.totalMembers > 0 ? (dept.totalRating / dept.totalMembers).toFixed(2) : 0,
            avgProductivity: dept.totalMembers > 0 ? Math.round(dept.totalProductivity / dept.totalMembers) : 0,
            members: dept.totalMembers
        }));
    };

    // Prepare team overview metrics for bar chart
    const prepareTeamOverviewMetrics = () => {
        const ratedMembers = filteredData.filter(m => m.rating && parseFloat(m.rating) > 0);
        const avgRating = ratedMembers.length > 0
            ? (ratedMembers.reduce((sum, m) => sum + parseFloat(m.rating), 0) / ratedMembers.length) * 20 // Convert to 100 scale
            : 0;

        const avgProductivity = filteredData.length > 0
            ? filteredData.reduce((sum, m) => sum + (m.productivity || 0), 0) / filteredData.length
            : 0;

        const totalGoals = filteredData.reduce((sum, m) => sum + (m.total_goals || 0), 0);
        const completedGoals = filteredData.reduce((sum, m) => sum + (m.completed_goals || 0), 0);
        const avgGoalCompletion = totalGoals > 0 ? (completedGoals / totalGoals) * 100 : 0;

        const topPerformersPercent = ratedMembers.length > 0
            ? (ratedMembers.filter(m => parseFloat(m.rating) >= 4.5).length / ratedMembers.length) * 100
            : 0;

        return [
            {
                metric: 'Avg Performance Rating',
                score: Math.round(avgRating),
                display: `${(avgRating / 20).toFixed(1)}/5.0`,
                color: '#10B981'
            },
            {
                metric: 'Avg Productivity',
                score: Math.round(avgProductivity),
                display: `${Math.round(avgProductivity)}%`,
                color: '#3B82F6'
            },
            {
                metric: 'Goal Completion Rate',
                score: Math.round(avgGoalCompletion),
                display: `${Math.round(avgGoalCompletion)}%`,
                color: '#F59E0B'
            },
            {
                metric: 'Top Performers',
                score: Math.round(topPerformersPercent),
                display: `${Math.round(topPerformersPercent)}%`,
                color: '#8B5CF6'
            }
        ];
    };

    // Calculate team stats
    const calculateStats = () => {
        const ratedMembers = filteredData.filter(m => m.rating && parseFloat(m.rating) > 0);
        const avgRating = ratedMembers.length > 0
            ? ratedMembers.reduce((sum, m) => sum + parseFloat(m.rating), 0) / ratedMembers.length
            : 0;

        const avgProductivity = filteredData.length > 0
            ? filteredData.reduce((sum, m) => sum + (m.productivity || 0), 0) / filteredData.length
            : 0;

        const topPerformers = ratedMembers.filter(m => parseFloat(m.rating) >= 4.5).length;
        const needsAttention = ratedMembers.filter(m => parseFloat(m.rating) < 3.5).length;

        const totalGoals = filteredData.reduce((sum, m) => sum + (m.total_goals || 0), 0);
        const completedGoals = filteredData.reduce((sum, m) => sum + (m.completed_goals || 0), 0);
        const avgGoalCompletion = totalGoals > 0 ? (completedGoals / totalGoals) * 100 : 0;

        return {
            avgRating,
            avgProductivity,
            topPerformers,
            needsAttention,
            totalMembers: filteredData.length,
            avgGoalCompletion,
            totalGoals,
            completedGoals
        };
    };

    const ratingsDistribution = prepareRatingsDistribution();
    const productivityDistribution = prepareProductivityDistribution();
    const teamComparison = prepareTeamComparison();
    const performanceComparison = preparePerformanceComparison();
    const departmentComparison = prepareDepartmentComparison();
    const teamOverviewMetrics = prepareTeamOverviewMetrics();
    const stats = calculateStats();

    const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];
    const PRODUCTIVITY_COLORS = ['#059669', '#10B981', '#34D399', '#FCD34D', '#F87171'];

    // Export to CSV function
    const exportToCSV = () => {
        const headers = ['Employee', 'Department', 'Rating', 'Productivity', 'Completed Goals', 'Total Goals', 'Status'];
        const rows = filteredData
            .filter(member => member.rating && member.rating > 0)
            .map(member => {
                const rating = parseFloat(member.rating);
                const status = rating >= 4.5 ? 'Excellent' :
                             rating >= 4.0 ? 'Good' :
                             rating >= 3.5 ? 'Average' : 'Needs Attention';

                return [
                    `${member.first_name} ${member.last_name}`,
                    member.department_name || 'N/A',
                    rating.toFixed(1),
                    member.productivity || 0,
                    member.completed_goals || 0,
                    member.total_goals || 0,
                    status
                ];
            });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `team-performance-analytics-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3">
                    {label && <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{label}</p>}
                    {payload.map((entry, index) => (
                        <p key={index} className="text-xs text-gray-600 dark:text-gray-400">
                            <span className="inline-block w-3 h-3 rounded-full mr-1" style={{ backgroundColor: entry.color }}></span>
                            {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Performance comparison tooltip
    const PerformanceTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{label}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="inline-block w-3 h-3 rounded-full mr-1 bg-blue-500"></span>
                        Rating: {data.ratingDisplay}/5.0 ({data.rating}%)
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="inline-block w-3 h-3 rounded-full mr-1 bg-green-500"></span>
                        Productivity: {data.productivity}%
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 pt-1 border-t border-gray-200 dark:border-gray-600">
                        Goals Completed: {data.goals}
                    </p>
                </div>
            );
        }
        return null;
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            cycle: 'all',
            department: 'all',
            ratingRange: 'all',
            performanceStatus: 'all'
        });
    };

    const hasActiveFilters = Object.values(filters).some(v => v !== 'all');

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
        );
    }

    if (teamPerformance.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                <Users className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Team Data</h3>
                <p className="text-gray-600 dark:text-gray-400">
                    No team performance data available yet.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <TrendingUp className="w-6 h-6" />
                        Team Performance Analytics
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Comprehensive insights into your team's performance and productivity
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Info Button */}
                    <div className="relative">
                        <button
                            onClick={() => setShowProductivityInfo(!showProductivityInfo)}
                            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            title="Productivity Calculation Info"
                        >
                            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </button>

                        {/* Info Tooltip */}
                        {showProductivityInfo && (
                            <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 z-50">
                                <div className="flex items-start justify-between mb-3">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                        Productivity Calculation
                                    </h4>
                                    <button
                                        onClick={() => setShowProductivityInfo(false)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="space-y-3 text-xs text-gray-600 dark:text-gray-400">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                                        <p className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Formula:</p>
                                        <p className="font-mono text-blue-800 dark:text-blue-400">
                                            Productivity = (Rating ÷ 5 × 60%) + (Goal Completion × 40%)
                                        </p>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900 dark:text-white mb-1">Components:</p>
                                        <ul className="space-y-1 ml-4 list-disc">
                                            <li><span className="font-medium">Performance Rating (60%)</span>: Based on review ratings from 0-5.0</li>
                                            <li><span className="font-medium">Goal Completion (40%)</span>: Percentage of completed goals</li>
                                        </ul>
                                    </div>
                                    <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                                        <p className="font-semibold text-gray-900 dark:text-white mb-1">Example:</p>
                                        <p>If an employee has a rating of 4.5/5.0 and 80% goal completion:</p>
                                        <p className="font-mono text-gray-800 dark:text-gray-300 mt-1">
                                            (4.5 ÷ 5 × 60) + (80 ÷ 100 × 40) = 54 + 32 = 86%
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Export Button */}
                    <button
                        onClick={exportToCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
                        title="Export to CSV"
                    >
                        <Download className="w-4 h-4" />
                        Export
                    </button>

                    {/* Refresh Button */}
                    <button
                        onClick={fetchTeamPerformance}
                        className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        title="Refresh Data"
                    >
                        <RefreshCw className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>

                    {/* Filters Toggle */}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors text-sm font-medium ${
                            hasActiveFilters
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                    >
                        <Filter className="w-4 h-4" />
                        Filters
                        {hasActiveFilters && <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">●</span>}
                        <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Department Filter */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Department
                            </label>
                            <select
                                value={filters.department}
                                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="all">All Departments</option>
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>

                        {/* Rating Range Filter */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Rating Range
                            </label>
                            <select
                                value={filters.ratingRange}
                                onChange={(e) => setFilters({ ...filters, ratingRange: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="all">All Ratings</option>
                                <option value="excellent">Excellent (4.5-5.0)</option>
                                <option value="good">Good (4.0-4.4)</option>
                                <option value="average">Average (3.5-3.9)</option>
                                <option value="below">Below Average (&lt;3.5)</option>
                            </select>
                        </div>

                        {/* Performance Status Filter */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Performance Status
                            </label>
                            <select
                                value={filters.performanceStatus}
                                onChange={(e) => setFilters({ ...filters, performanceStatus: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="all">All Statuses</option>
                                <option value="top">Top Performers</option>
                                <option value="attention">Needs Attention</option>
                            </select>
                        </div>

                        {/* Review Cycle Filter */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Review Cycle
                            </label>
                            <select
                                value={filters.cycle}
                                onChange={(e) => setFilters({ ...filters, cycle: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="all">All Cycles</option>
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="annual">Annual</option>
                            </select>
                        </div>
                    </div>

                    {/* Reset Filters */}
                    {hasActiveFilters && (
                        <div className="mt-3 flex justify-end">
                            <button
                                onClick={resetFilters}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            >
                                <X className="w-4 h-4" />
                                Reset Filters
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Filter Results Info */}
            {hasActiveFilters && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                        Showing {filteredData.length} of {teamPerformance.length} team members based on active filters
                    </p>
                </div>
            )}

            {/* Enhanced Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div
                    onClick={scrollToDetailedTable}
                    className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg shadow p-6 border border-blue-200 dark:border-blue-800 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                >
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Team Members</p>
                        <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <p className="text-3xl font-bold text-blue-700 dark:text-blue-400">{stats.totalMembers}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Active team size · Click to view details</p>
                </div>

                <div
                    onClick={scrollToDetailedTable}
                    className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg shadow p-6 border border-green-200 dark:border-green-800 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                >
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-green-900 dark:text-green-300">Average Rating</p>
                        <Award className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="text-3xl font-bold text-green-700 dark:text-green-400">{stats.avgRating.toFixed(1)}/5.0</p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">Team performance score · Click to view details</p>
                </div>

                <div
                    onClick={scrollToDetailedTable}
                    className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg shadow p-6 border border-purple-200 dark:border-purple-800 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                >
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-purple-900 dark:text-purple-300">Avg Productivity</p>
                        <Activity className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <p className="text-3xl font-bold text-purple-700 dark:text-purple-400">{Math.round(stats.avgProductivity)}%</p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">Overall productivity rate · Click to view details</p>
                </div>

                <div
                    onClick={scrollToDetailedTable}
                    className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg shadow p-6 border border-orange-200 dark:border-orange-800 cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                >
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-orange-900 dark:text-orange-300">Goal Completion</p>
                        <Target className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <p className="text-3xl font-bold text-orange-700 dark:text-orange-400">{Math.round(stats.avgGoalCompletion)}%</p>
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">{stats.completedGoals}/{stats.totalGoals} goals · Click to view details</p>
                </div>
            </div>

            {/* Additional Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-600" />
                            Top Performers
                        </h3>
                        <span className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.topPerformers}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                            className="bg-green-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${stats.totalMembers > 0 ? (stats.topPerformers / stats.totalMembers) * 100 : 0}%` }}
                        ></div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                        {stats.totalMembers > 0 ? Math.round((stats.topPerformers / stats.totalMembers) * 100) : 0}% with rating ≥ 4.5
                    </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <TrendingDown className="w-5 h-5 text-orange-600" />
                            Needs Attention
                        </h3>
                        <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.needsAttention}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                            className="bg-orange-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${stats.totalMembers > 0 ? (stats.needsAttention / stats.totalMembers) * 100 : 0}%` }}
                        ></div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                        {stats.totalMembers > 0 ? Math.round((stats.needsAttention / stats.totalMembers) * 100) : 0}% with rating &lt; 3.5
                    </p>
                </div>
            </div>

            {/* Productivity Trends Chart - Issue #16 */}
            <ProductivityTrendChart />

            {/* Team Metrics Overview - Horizontal Bar Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    Team Performance Summary
                </h3>
                <div className="space-y-6">
                    {teamOverviewMetrics.map((item, index) => (
                        <div key={index} className="space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.metric}</span>
                                <span className="text-sm font-bold text-gray-900 dark:text-white">{item.display}</span>
                            </div>
                            <div className="relative w-full h-8 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                                <div
                                    className="absolute inset-y-0 left-0 rounded-lg transition-all duration-700 ease-out flex items-center justify-end pr-3"
                                    style={{
                                        width: `${item.score}%`,
                                        background: `linear-gradient(90deg, ${item.color}99, ${item.color})`
                                    }}
                                >
                                    <span className="text-xs font-semibold text-white drop-shadow">
                                        {item.score}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                        All metrics normalized to 100% scale for easy comparison
                    </p>
                </div>
            </div>

            {/* Charts Grid - Donut Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Ratings Distribution Donut Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-green-600 dark:text-green-400" />
                        Performance Ratings Distribution
                    </h3>
                    <div className="relative">
                        <ResponsiveContainer width="100%" height={320}>
                            <PieChart>
                                <Pie
                                    data={ratingsDistribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={({ name, percent, cx, cy, midAngle, innerRadius, outerRadius }) => {
                                        if (percent < 0.05) return '';
                                        const RADIAN = Math.PI / 180;
                                        const radius = outerRadius + 25;
                                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                        return (
                                            <text
                                                x={x}
                                                y={y}
                                                fill="#6B7280"
                                                textAnchor={x > cx ? 'start' : 'end'}
                                                dominantBaseline="central"
                                                className="text-xs font-medium"
                                            >
                                                {`${(percent * 100).toFixed(0)}%`}
                                            </text>
                                        );
                                    }}
                                >
                                    {ratingsDistribution.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                            stroke="rgba(255,255,255,0.8)"
                                            strokeWidth={2}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    iconType="circle"
                                    formatter={(value) => <span className="text-xs">{value}</span>}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        {/* Center Label */}
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none" style={{ marginTop: '-18px' }}>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalMembers}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Team Members</div>
                        </div>
                    </div>
                </div>

                {/* Productivity Distribution Donut Chart */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Activity className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        Productivity Distribution
                    </h3>
                    <div className="relative">
                        <ResponsiveContainer width="100%" height={320}>
                            <PieChart>
                                <Pie
                                    data={productivityDistribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={({ name, percent, cx, cy, midAngle, innerRadius, outerRadius }) => {
                                        if (percent < 0.05) return '';
                                        const RADIAN = Math.PI / 180;
                                        const radius = outerRadius + 25;
                                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                        return (
                                            <text
                                                x={x}
                                                y={y}
                                                fill="#6B7280"
                                                textAnchor={x > cx ? 'start' : 'end'}
                                                dominantBaseline="central"
                                                className="text-xs font-medium"
                                            >
                                                {`${(percent * 100).toFixed(0)}%`}
                                            </text>
                                        );
                                    }}
                                >
                                    {productivityDistribution.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={PRODUCTIVITY_COLORS[index % PRODUCTIVITY_COLORS.length]}
                                            stroke="rgba(255,255,255,0.8)"
                                            strokeWidth={2}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    iconType="circle"
                                    formatter={(value) => <span className="text-xs">{value}</span>}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        {/* Center Label */}
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none" style={{ marginTop: '-18px' }}>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">{Math.round(stats.avgProductivity)}%</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Avg Score</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Department Comparison Chart */}
            {departmentComparison.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        Department Performance Comparison
                    </h3>
                    <ResponsiveContainer width="100%" height={380}>
                        <BarChart data={departmentComparison} barGap={8}>
                            <defs>
                                <linearGradient id="ratingGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.8}/>
                                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.4}/>
                                </linearGradient>
                                <linearGradient id="productivityGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.8}/>
                                    <stop offset="100%" stopColor="#10B981" stopOpacity={0.4}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} vertical={false} />
                            <XAxis
                                dataKey="department"
                                stroke="#6B7280"
                                style={{ fontSize: '12px', fontWeight: 500 }}
                                tickLine={false}
                            />
                            <YAxis
                                yAxisId="left"
                                stroke="#3B82F6"
                                style={{ fontSize: '11px' }}
                                tickLine={false}
                                label={{ value: 'Avg Rating (0-5)', angle: -90, position: 'insideLeft', style: { fontSize: '11px', fill: '#6B7280', fontWeight: 500 } }}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                stroke="#10B981"
                                style={{ fontSize: '11px' }}
                                tickLine={false}
                                label={{ value: 'Avg Productivity (%)', angle: 90, position: 'insideRight', style: { fontSize: '11px', fill: '#6B7280', fontWeight: 500 } }}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.05)' }} />
                            <Legend wrapperStyle={{ paddingTop: '20px' }} />
                            <Bar yAxisId="left" dataKey="avgRating" fill="url(#ratingGradient)" name="Avg Rating" radius={[8, 8, 0, 0]} />
                            <Bar yAxisId="right" dataKey="avgProductivity" fill="url(#productivityGradient)" name="Avg Productivity %" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Top Performers Bar Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    Top 10 Performers
                </h3>
                <ResponsiveContainer width="100%" height={450}>
                    <BarChart data={teamComparison} layout="vertical" margin={{ left: 20 }}>
                        <defs>
                            <linearGradient id="performerGradient" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                                <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.8}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} horizontal={false} />
                        <XAxis
                            type="number"
                            domain={[0, 5]}
                            ticks={[0, 1, 2, 3, 4, 5]}
                            stroke="#6B7280"
                            style={{ fontSize: '12px' }}
                            tickLine={false}
                            label={{ value: 'Performance Rating', position: 'insideBottom', offset: -5, style: { fontSize: '11px', fill: '#6B7280', fontWeight: 500 } }}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            stroke="#6B7280"
                            style={{ fontSize: '12px', fontWeight: 500 }}
                            width={140}
                            tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(139, 92, 246, 0.05)' }} />
                        <Bar
                            dataKey="rating"
                            fill="url(#performerGradient)"
                            name="Rating"
                            radius={[0, 8, 8, 0]}
                            label={({ value }) => value.toFixed(1)}
                            labelPosition="right"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Performance Rating vs Productivity Comparison */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        Performance Rating vs Productivity Comparison
                    </h3>
                    <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded" style={{ background: 'linear-gradient(90deg, #3B82F6, #60A5FA)' }}></div>
                            <span className="text-gray-600 dark:text-gray-400">Rating (0-100)</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded" style={{ background: 'linear-gradient(90deg, #10B981, #34D399)' }}></div>
                            <span className="text-gray-600 dark:text-gray-400">Productivity %</span>
                        </div>
                    </div>
                </div>
                <ResponsiveContainer width="100%" height={500}>
                    <BarChart data={performanceComparison} margin={{ top: 10, right: 30, left: 0, bottom: 70 }}>
                        <defs>
                            <linearGradient id="ratingBar" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.9}/>
                                <stop offset="100%" stopColor="#60A5FA" stopOpacity={0.7}/>
                            </linearGradient>
                            <linearGradient id="productivityBar" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#10B981" stopOpacity={0.9}/>
                                <stop offset="100%" stopColor="#34D399" stopOpacity={0.7}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} vertical={false} />
                        <XAxis
                            dataKey="name"
                            stroke="#6B7280"
                            style={{ fontSize: '11px', fontWeight: 500 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[0, 100]}
                            stroke="#6B7280"
                            style={{ fontSize: '12px' }}
                            tickLine={false}
                            label={{ value: 'Score (0-100)', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#6B7280', fontWeight: 500 } }}
                        />
                        <Tooltip content={<PerformanceTooltip />} cursor={{ fill: 'rgba(99, 102, 241, 0.05)' }} />
                        <Legend
                            wrapperStyle={{ paddingTop: '10px' }}
                            iconType="rect"
                            iconSize={12}
                        />
                        <Bar
                            dataKey="rating"
                            fill="url(#ratingBar)"
                            name="Performance Rating (0-100)"
                            radius={[8, 8, 0, 0]}
                            maxBarSize={40}
                        />
                        <Bar
                            dataKey="productivity"
                            fill="url(#productivityBar)"
                            name="Productivity Score"
                            radius={[8, 8, 0, 0]}
                            maxBarSize={40}
                        />
                    </BarChart>
                </ResponsiveContainer>
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                                <p className="text-xs font-semibold text-green-900 dark:text-green-300">High Performers</p>
                            </div>
                            <p className="text-xs text-green-700 dark:text-green-400 ml-4">
                                Both Rating & Productivity ≥ 70
                            </p>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                <p className="text-xs font-semibold text-blue-900 dark:text-blue-300">Average Performers</p>
                            </div>
                            <p className="text-xs text-blue-700 dark:text-blue-400 ml-4">
                                Either metric between 50-70
                            </p>
                        </div>
                        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                                <p className="text-xs font-semibold text-orange-900 dark:text-orange-300">Needs Improvement</p>
                            </div>
                            <p className="text-xs text-orange-700 dark:text-orange-400 ml-4">
                                Any metric below 50
                            </p>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-3">
                        Both metrics normalized to 0-100 scale • Rating: (5.0 scale × 20) • Productivity: Direct percentage
                    </p>
                </div>
            </div>

            {/* Team Comparison Table */}
            <div ref={detailedTableRef} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700 scroll-mt-4">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Detailed Team Comparison
                    </h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Employee
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Rating
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Productivity
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Goals
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {teamComparison.map((member, idx) => (
                                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                        {member.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className={`font-semibold ${
                                                member.rating >= 4.5 ? 'text-green-600 dark:text-green-400' :
                                                member.rating >= 4.0 ? 'text-blue-600 dark:text-blue-400' :
                                                member.rating >= 3.5 ? 'text-yellow-600 dark:text-yellow-400' :
                                                'text-orange-600 dark:text-orange-400'
                                            }`}>
                                                {member.rating.toFixed(1)}/5.0
                                            </span>
                                            <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full ${
                                                        member.rating >= 4.5 ? 'bg-green-600' :
                                                        member.rating >= 4.0 ? 'bg-blue-600' :
                                                        member.rating >= 3.5 ? 'bg-yellow-600' : 'bg-orange-600'
                                                    }`}
                                                    style={{ width: `${(member.rating / 5) * 100}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-900 dark:text-white">{member.productivity}%</span>
                                            <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-purple-600 rounded-full"
                                                    style={{ width: `${member.productivity}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                                        {member.goalsCompleted}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            member.rating >= 4.5 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                            member.rating >= 4.0 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                            member.rating >= 3.5 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                            'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                        }`}>
                                            {member.rating >= 4.5 ? 'Excellent' :
                                             member.rating >= 4.0 ? 'Good' :
                                             member.rating >= 3.5 ? 'Average' : 'Needs Attention'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TeamPerformanceAnalytics;
